import React, { useState } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Search, BookOpen, Clock, MapPin, Award, Users, TrendingUp, ArrowRight } from 'lucide-react';
import { courses } from '../../data/mockData';
import { centerToast } from '../../components/CenterToast';

const CATEGORY_STYLE = {
  'Islamic Studies':   { icon: BookOpen,   bg: 'bg-primary',      text: 'text-primary-foreground' },
  'Professional':      { icon: Award,      bg: 'bg-indigo-900',   text: 'text-white' },
  'Community Skills':  { icon: Users,      bg: 'bg-amber-500',    text: 'text-white' },
  'Impact':            { icon: TrendingUp, bg: 'bg-teal-500',     text: 'text-white' }
};

const CATEGORIES = ['All', 'Islamic Studies', 'Professional', 'Community Skills', 'Impact'];

export default function Learn() {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredCourses = courses.filter((course) => {
    const matchesCategory = selectedCategory === 'All' || course.category === selectedCategory;
    const matchesSearch = course.title.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const handleFindOutMore = (courseTitle) => {
    centerToast(`"${courseTitle}" — details coming soon`, '🛠️');
  };

  return (
    <div className="min-h-screen bg-background page-fade-in">
      {/* Header */}
      <div className="bg-card border-b px-4 py-6">
        <h1 className="text-3xl font-bold mb-2">Learn</h1>
        <p className="text-muted-foreground">Explore MTFA&apos;s community learning catalogue</p>
      </div>

      {/* Search Bar */}
      <div className="px-4 py-4 bg-card">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            data-testid="learn-search"
            placeholder="Search courses..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-11"
          />
        </div>
      </div>

      {/* Category Filters */}
      <div className="px-4 py-3 bg-card border-b">
        <div className="flex gap-2 overflow-x-auto horizontal-scroll pb-2">
          {CATEGORIES.map((category) => (
            <Button
              key={category}
              data-testid={`learn-filter-${category.toLowerCase().replace(/\s+/g, '-')}`}
              variant={selectedCategory === category ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedCategory(category)}
              className="whitespace-nowrap"
            >
              {category}
            </Button>
          ))}
        </div>
      </div>

      {/* Course Grid */}
      <div className="p-4 space-y-4">
        {filteredCourses.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-10">
            No courses match your search.
          </p>
        )}
        {filteredCourses.map((course) => {
          const style = CATEGORY_STYLE[course.category] || CATEGORY_STYLE['Islamic Studies'];
          const Icon = style.icon;
          return (
            <Card key={course.id} data-testid={`course-${course.id}`} className="overflow-hidden card-hover">
              {/* Coloured icon block replacing the previous image */}
              <div className={`relative h-32 flex items-center justify-center ${style.bg}`}>
                <Icon className={`h-16 w-16 ${style.text} opacity-90`} strokeWidth={1.5} />
                {course.badge && (
                  <div className="absolute top-3 left-3">
                    <Badge className="bg-white/90 text-foreground hover:bg-white/90">
                      {course.badge}
                    </Badge>
                  </div>
                )}
                <div className="absolute top-3 right-3">
                  <Badge variant="outline" className="bg-white/90 border-transparent text-foreground">
                    {course.level}
                  </Badge>
                </div>
              </div>

              <CardContent className="p-4 space-y-3">
                <div>
                  <h3 className="font-semibold text-lg leading-tight mb-1">{course.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{course.description}</p>
                </div>

                {/* Meta row */}
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    <span>{course.duration}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <MapPin className="h-4 w-4" />
                    <span>{course.format}</span>
                  </div>
                </div>

                <Button
                  className="w-full"
                  size="lg"
                  data-testid={`course-cta-${course.id}`}
                  onClick={() => handleFindOutMore(course.title)}
                >
                  Find out more
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
